import { PartialNullable, XMLHttpRequestLike } from "../Extensions";
import { PropertyChange } from "../PropertyChange";
import { IRadObject, Property, PropertyInt32, PropertyObject, PropertyString, RadObject, RadObjectPropertyValues } from "../RadObject";
import { NAMESPACE } from "./_Namespace";

export interface IValidationError
{
    id: string;
    entityName: string;
    errorMessage: string;
    propertyName: string;
}

export type AjaxErrorPropertyValues =
    RadObjectPropertyValues
    & {
        status: number;
        type: string;
        message: string;
        stackTrace: string;
        validationErrors: IValidationError[];
        reason: string;
        subReason: string;
        data: any;
        additionalData: any;
    };

export type AjaxErrorProperties = {
    [name in keyof AjaxErrorPropertyValues]: Property<AjaxErrorPropertyValues[name]>
};

export interface IAjaxError extends IRadObject, AjaxErrorProperties
{
}

export class AjaxError extends RadObject implements IAjaxError
{
    // #region Static

    public static override classNamespace = NAMESPACE + "AjaxError";
    public static override className = "AjaxError";

    // #region Public Methods

    public static fromObject(object: any, xhr: XMLHttpRequestLike)
    {
        let
            status: number | undefined,
            type: string,
            message: string | undefined,
            stackTrace: string | undefined,
            validationErrors: IValidationError[] | undefined,
            reason: string | undefined,
            subReason: string | undefined,
            data: any,
            additionalData: any;

        if (xhr)
            status = xhr.status;

        if (object.error)
            object = object.error;

        type = object.type || "Unknown Error";

        if (typeof object.message === "string")
            message = object.message;

        if (typeof object.stackTrace === "string")
            stackTrace = object.stackTrace;

        if (object.validationErrors)
            validationErrors = object.validationErrors;

        if (typeof object.reason === "string")
            reason = object.reason;

        if (typeof object.subReason === "string")
            subReason = object.subReason;

        if (object.data)
            data = object.data;

        if (object.additionalData)
            additionalData = object.additionalData;

        return new AjaxError({ additionalData, data, message, reason, stackTrace, status, subReason, type, validationErrors });
    }
    public static fromErrorMessage(errorMessage: string, xhr?: XMLHttpRequestLike)
    {
        let
            status: number | undefined,
            type: string,
            message: string;

        if (xhr)
            status = xhr.status;

        switch (status)
        {
            case 404:
                type = "NotFoundApiException";
                message = "The resource was not found";
                break;
            default:
                type = "UnknownApiException";
                message = errorMessage;
                break;
        }

        return new AjaxError({ message, status, type });
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public status!: PropertyInt32;
    public type!: PropertyString;
    public message!: PropertyString;
    public stackTrace!: PropertyString;
    public validationErrors!: Property<IValidationError[]>;
    public reason!: PropertyString;
    public subReason!: PropertyString;
    public data!: PropertyObject;
    public additionalData!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<AjaxErrorPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Protected Methods

    protected _additionalDataChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _dataChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _messageChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _reasonChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _stackTraceChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _statusChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }
    protected _subReasonChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _typeChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _validationErrorsChanged(change: PropertyChange<IValidationError[]>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: AjaxErrorPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.status = new PropertyInt32(this, propertyValues.status, this._statusChanged);
        this.type = new PropertyString(this, propertyValues.type, this._typeChanged);
        this.message = new PropertyString(this, propertyValues.message, this._messageChanged);
        this.stackTrace = new PropertyString(this, propertyValues.stackTrace, this._stackTraceChanged);
        this.validationErrors = new Property<IValidationError[]>(this, "System.Object, System.Private.CoreLib", propertyValues.validationErrors, this._validationErrorsChanged);
        this.reason = new PropertyString(this, propertyValues.reason, this._reasonChanged);
        this.subReason = new PropertyString(this, propertyValues.subReason, this._subReasonChanged);
        this.data = new PropertyObject(this, propertyValues.data, this._dataChanged);
        this.additionalData = new PropertyObject(this, propertyValues.additionalData, this._additionalDataChanged);
    }

    // #endregion

    // #endregion
}